import { useCallback } from 'react';
import useTimerCore from './useTimerCore';

/**
 * Segment data returned from completeSegment
 */
interface SegmentData {
  /** Time in milliseconds */
  time: number;
  /** Distance in meters */
  distance: number;
  /** Timestamp when segment was recorded */
  timestamp: number;
}

/**
 * Options for segment timer hook
 */
interface SegmentTimerOptions {
  /** External running state if provided */
  isRunning?: boolean | null;
  /** Optional callback when segment is completed */
  onSegmentCompleted?: (segment: SegmentData) => void;
}

/**
 * Return type for useSegmentTimer hook
 */
interface SegmentTimerResult {
  /** Current segment time in milliseconds */
  segmentTime: number;
  /** Whether the timer is running */
  isRunning: boolean;
  /** Reset only the segment timer without affecting other timers */
  resetSegmentTimer: () => void;
  /** Complete a segment with specified distance */
  completeSegment: (
    distance: number,
    callback?: (segment: SegmentData) => void
  ) => SegmentData | null;
  /** Start segment timer if using internal state */
  startSegmentTimer: () => void;
  /** Pause segment timer if using internal state */
  pauseSegmentTimer: () => void;
  /** Internal segment timer for direct access */
  segmentTimer: ReturnType<typeof useTimerCore>;
}

/**
 * Custom hook for segment timer functionality
 * Manages a timer specifically for tracking segments within laps
 *
 * @param options - Configuration options
 * @returns Segment timer state and methods
 */
const useSegmentTimer = ({
  isRunning: externalIsRunning = null,
  onSegmentCompleted,
}: SegmentTimerOptions = {}): SegmentTimerResult => {
  // Use timer core for the segment timer
  const segmentTimer = useTimerCore();

  // Use either external running state or internal timer state
  const isRunning = externalIsRunning !== null ? externalIsRunning : segmentTimer.isRunning;

  /**
   * Reset only the segment timer without affecting other timers
   */
  const resetSegmentTimer = useCallback(() => {
    if (isRunning) {
      segmentTimer.resetTimer();

      // Start a new segment timer
      setTimeout(() => {
        segmentTimer.startNewTimer();
      }, 0);
    }
  }, [isRunning, segmentTimer]);

  /**
   * Complete a segment with specified distance
   *
   * @param distance - The distance for the completed segment in meters
   * @returns Completed segment data
   */
  const completeSegment = useCallback(
    (distance: number, callback?: (segment: SegmentData) => void): SegmentData | null => {
      if (isRunning && distance > 0) {
        const segmentData: SegmentData = {
          time: segmentTimer.time,
          distance,
          timestamp: Date.now(),
        };

        // Reset segment timer
        resetSegmentTimer();

        // Call the callback if provided
        if (callback || onSegmentCompleted) {
          const cb = callback || onSegmentCompleted;
          cb?.(segmentData);
        }

        return segmentData;
      }
      return null;
    },
    [isRunning, segmentTimer, resetSegmentTimer, onSegmentCompleted]
  );

  /**
   * Start segment timer if using internal state
   */
  const startSegmentTimer = useCallback(() => {
    if (externalIsRunning === null) {
      segmentTimer.startTimer();
    }
  }, [segmentTimer, externalIsRunning]);

  /**
   * Pause segment timer if using internal state
   */
  const pauseSegmentTimer = useCallback(() => {
    if (externalIsRunning === null) {
      segmentTimer.pauseTimer();
    }
  }, [segmentTimer, externalIsRunning]);

  return {
    segmentTime: segmentTimer.time,
    isRunning,
    resetSegmentTimer,
    completeSegment,
    startSegmentTimer,
    pauseSegmentTimer,
    // Expose the internal timer for direct access if needed
    segmentTimer,
  };
};

export default useSegmentTimer;
